import { useRef, useMemo, useEffect, type FC } from 'react';
import * as echarts from 'echarts';
import { CounterCard } from '../ui';
import type { ChartDataPoint } from '@smolt/shared/types';
import { useBreakpoint } from '@smolt/shared/hooks';
import { StatisticsGrid } from './StatisticsGrid';

export interface CounterChartCardProps {
  data: ChartDataPoint[];
  currentCount?: number;
  weight?: number;
  weightPerFish?: number;
  tonnesPerHour?: number;
  fishPerHour?: number;
  /** @deprecated Use sessionStart instead for live duration */
  duration?: string;
  sessionStart?: number | null;
  isCounting?: boolean;
}

interface ChartConfig {
  tickInterval: number; // X-axis tick interval in seconds
  aggregation: number; // Data aggregation granularity in seconds
  format: 'mm:ss' | 'hh:mm'; // Time format for labels
  maxPoints: number; // Maximum data points to display
  maxLabels: number; // Maximum labels to show
}

function getChartConfig(
  elapsedSeconds: number,
  breakpoint: 'mobile' | 'tablet' | 'desktop' | 'largeDesktop'
): ChartConfig {
  // Determine max labels based on screen width
  const maxLabels = breakpoint === 'mobile' ? 5 : breakpoint === 'tablet' ? 8 : 12;

  // Candidate intervals as requested: 1s, 10s, 30s, 10m(600), 30m(1800), 1h(3600)
  const candidates = [1, 10, 30, 600, 1800, 3600];

  // Find the smallest interval that results in <= maxLabels
  let bestInterval = candidates[candidates.length - 1];
  for (const interval of candidates) {
    const estimatedLabels = elapsedSeconds / interval;
    if (estimatedLabels <= maxLabels) {
      bestInterval = interval;
      break;
    }
  }

  // Determine aggregation and format based on duration
  // Keep aggregation simple for now
  let aggregation = 1;
  let format: 'mm:ss' | 'hh:mm' = 'mm:ss';
  const maxPoints = 360;

  if (elapsedSeconds > 3600) {
    format = 'hh:mm';
    aggregation = 60; // Aggregate more for long sessions
  } else if (elapsedSeconds > 600) {
    aggregation = 10;
  }

  return {
    tickInterval: bestInterval,
    aggregation,
    format,
    maxPoints,
    maxLabels,
  };
}

function formatTime(seconds: number, format: ChartConfig['format']): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (format === 'mm:ss') {
    const totalMins = Math.floor(seconds / 60);
    return `${String(totalMins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

function aggregateData(
  rawData: ChartDataPoint[],
  aggregationSeconds: number
): Array<{ elapsed: number; count: number }> {
  if (rawData.length === 0) return [];

  // Always use bucketing to ensure deduplication of timestamps
  const interval = aggregationSeconds <= 1 ? 1 : aggregationSeconds;

  const buckets = new Map<number, Array<{ elapsed: number; count: number }>>();
  for (const point of rawData) {
    const [minutes, seconds] = point.time.split(':').map(Number);
    const elapsed = minutes * 60 + (seconds || 0);
    const key = Math.floor(elapsed / interval) * interval;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push({ elapsed, count: point.count });
  }

  const result = Array.from(buckets.entries())
    .map(([bucketKey, points]) => {
      const lastPoint = points[points.length - 1];
      // Use bucket key for elapsed to ensure alignment with tick intervals
      return { elapsed: bucketKey, count: Math.floor(lastPoint.count) };
    })
    .sort((a, b) => a.elapsed - b.elapsed);

  // Don't prepend (0, 0) point - use actual data range for windowed views
  // This prevents the "sudden increase" visual artifact when showing partial data
  if (result.length === 0) {
    result.push({ elapsed: 0, count: 0 });
  }
  return result;
}

export const CounterChartCard: FC<CounterChartCardProps> = ({
  data,
  currentCount = 0,
  weight = 0,
  weightPerFish = 1.45,
  tonnesPerHour = 0,
  fishPerHour = 0,
  sessionStart = null,
  isCounting = false,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const breakpoint = useBreakpoint();

  const isMobile = breakpoint === 'mobile';
  const isTablet = breakpoint === 'tablet';

  const getChartContainerHeight = () => (isMobile ? 120 : isTablet ? 140 : 160);
  const chartContainerHeight = getChartContainerHeight();

  // Calculate elapsed seconds from data timestamps (stable, doesn't cause re-renders)
  const elapsedSeconds = useMemo(() => {
    if (data.length < 2) return 0;
    const firstPoint = data[0] as { timestamp?: number; time?: string };
    const lastPoint = data[data.length - 1] as { timestamp?: number; time?: string };

    // If timestamps are available, use them
    if (firstPoint.timestamp && lastPoint.timestamp) {
      return Math.floor((lastPoint.timestamp - firstPoint.timestamp) / 1000);
    }

    // Fallback: parse time strings (MM:SS format)
    const parseTimeToSeconds = (timeStr: string): number => {
      const parts = timeStr.split(':');
      if (parts.length === 2) {
        return parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
      if (parts.length === 3) {
        return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
      }
      return 0;
    };

    return (
      parseTimeToSeconds(lastPoint.time || '00:00') - parseTimeToSeconds(firstPoint.time || '00:00')
    );
  }, [data]);
  const chartConfig = useMemo(
    () => getChartConfig(elapsedSeconds, breakpoint),
    [elapsedSeconds, breakpoint]
  );

  const { chartData, maxValue } = useMemo(() => {
    if (!data || data.length === 0) return { chartData: [], maxValue: 0 };
    const aggregatedData = aggregateData(data, chartConfig.aggregation);
    const max = aggregatedData.length > 0 ? Math.max(...aggregatedData.map((d) => d.count), 0) : 0;
    return { chartData: aggregatedData, maxValue: max };
  }, [data, chartConfig]);

  const yAxisMax = useMemo(() => {
    if (maxValue === 0) return 100;
    const rawMax = maxValue * 1.15;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawMax)));
    const normalized = rawMax / magnitude;
    let niceMultiplier;
    if (normalized <= 1) niceMultiplier = 1;
    else if (normalized <= 2) niceMultiplier = 2;
    else if (normalized <= 4) niceMultiplier = 4;
    else if (normalized <= 8) niceMultiplier = 8;
    else niceMultiplier = 10;
    return niceMultiplier * magnitude;
  }, [maxValue]);

  const xAxisFontSize = useMemo(() => (isMobile ? 8 : isTablet ? 9 : 10), [isMobile, isTablet]);

  const chartLabels = useMemo(() => chartData.map((d) => d.elapsed), [chartData]);
  const chartValues = useMemo(() => chartData.map((d) => d.count), [chartData]);

  const option: echarts.EChartsOption = useMemo(
    () => ({
      grid: { top: 30, right: 20, bottom: 25, left: 50, containLabel: false },
      xAxis: {
        type: 'category',
        data: chartLabels,
        boundaryGap: false, // Place data points directly on axis marks for accurate labels
        axisLine: { show: true, lineStyle: { color: '#E5E5EA' } },
        axisTick: { show: false },
        axisLabel: {
          show: true,
          color: '#8E8E93',
          fontFamily: 'Noto Sans',
          fontWeight: 'bold',
          fontSize: xAxisFontSize,
          showMinLabel: true,
          showMaxLabel: true,
          hideOverlap: false, // Ensure all labels are shown
          formatter: (value: number | string, index: number) => {
            const seconds = typeof value === 'string' ? parseInt(value) : value;
            // Show label at tick intervals, first label, and last label
            const isFirstOrLast = index === 0 || index === chartLabels.length - 1;
            const isTickInterval = seconds % chartConfig.tickInterval === 0;
            if (isFirstOrLast || isTickInterval) {
              return formatTime(seconds, chartConfig.format);
            }
            return '';
          },
          interval: 0, // Check all labels, let formatter decide what to show
        },
      },
      yAxis: {
        type: 'value',
        show: true,
        max: yAxisMax,
        min: 0,
        interval: yAxisMax / 4,
        splitLine: {
          show: true,
          lineStyle: { color: 'rgba(199, 199, 204, 1)', width: 0.5, type: 'solid' },
        },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
      series: [
        {
          type: 'line',
          data: chartValues,
          smooth: false,
          symbol: 'circle',
          symbolSize: 8,
          showSymbol: false,
          itemStyle: { color: '#007AFF', borderColor: '#FFFFFF', borderWidth: 2 },
          lineStyle: { color: '#007AFF', width: 3 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(0, 122, 255, 0.5)' },
                { offset: 0.3, color: 'rgba(0, 122, 255, 0.35)' },
                { offset: 0.6, color: 'rgba(0, 122, 255, 0.2)' },
                { offset: 1, color: 'rgba(0, 122, 255, 0.05)' },
              ],
            },
          },
          emphasis: {
            disabled: false,
            scale: false,
            itemStyle: { color: '#007AFF', borderColor: '#000000', borderWidth: 2 },
            label: {
              show: true,
              position: 'top',
              distance: 8,
              formatter: (params: unknown) => {
                const p = params as { value?: number };
                return p.value !== undefined ? Math.floor(p.value).toLocaleString() : '';
              },
              color: '#007AFF',
              fontFamily: 'Noto Sans',
              fontWeight: 'bold',
              fontSize: isMobile ? 10 : 12,
            },
          },
        },
      ],
      tooltip: {
        trigger: 'axis',
        formatter: () => '',
        backgroundColor: 'transparent',
        borderWidth: 0,
        padding: 0,
        hideDelay: 2000,
        axisPointer: { type: 'line', lineStyle: { color: '#000000', width: 1, type: 'solid' } },
      },
      animation: true,
      animationDuration: isCounting ? 300 : 500,
      animationEasing: 'cubicOut',
    }),
    [chartLabels, chartValues, yAxisMax, xAxisFontSize, isCounting, chartConfig, isMobile]
  );

  // Initialize ECharts
  useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current, undefined, { renderer: 'canvas' });
    }

    const chart = chartInstanceRef.current;
    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver?.disconnect();
      chart?.dispose();
      chartInstanceRef.current = null;
    };
  }, []);

  // Update Chart
  useEffect(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.setOption(option, {
        notMerge: false,
        lazyUpdate: true,
        silent: true,
      });
    }
  }, [option]);

  return (
    <CounterCard
      variant="light"
      padding="none"
      className="overflow-hidden"
    >
      <div
        className="flex flex-col"
        style={{ padding: isTablet ? '2px 6px' : '4px 8px' }}
      >
        <h3
          style={{
            fontFamily: 'Noto Sans',
            fontWeight: 700,
            fontSize: isMobile ? 'clamp(12px, 3.8vw, 18px)' : 'clamp(12px, 3cqi, 20px)',
            lineHeight: isMobile ? 'clamp(18px, 5.7vw, 27px)' : 'clamp(18px, 4.5cqi, 30px)',
            letterSpacing: '0%',
            textAlign: 'left',
            verticalAlign: 'middle',
            color: '#8E8E93',
            margin: 0,
            marginBottom: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(8px, 1.6cqh, 20px)',
          }}
        >
          Chart
        </h3>

        <div
          className="relative w-full"
          style={{
            background: 'white',
            containerType: 'inline-size',
            overflow: 'visible',
            padding: isTablet ? '2px 6px' : '8px 12px',
            marginBottom: isTablet ? '2px' : '8px',
            height: `${chartContainerHeight}px`,
            flex: '0 0 auto',
          }}
        >
          <div
            ref={chartRef}
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        <StatisticsGrid
          currentCount={currentCount}
          weight={weight}
          weightPerFish={weightPerFish}
          tonnesPerHour={tonnesPerHour}
          fishPerHour={fishPerHour}
          sessionStart={sessionStart}
          isCounting={isCounting}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      </div>
    </CounterCard>
  );
};
